import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UseFormTrigger } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { COUNTRY_CODES } from "~/common/constants/verificationCode";
import { useFormatHook } from "~/patients/hooks/useFormatHook";
import { signUpTeamMember } from "~/providers/api";
import { ROUTES } from "~/providers/router";
import { useOnSignUpTeamMemberStore } from "~/providers/stores/useOnSignUpTeamMemberStore";
import { TeamMemberSignUpData } from "~/providers/utils";
import { TeamMemberSignUpSchemaType } from "~/providers/utils/teamMemberSignUpSchema";

interface useSubmitTeamMemberSignUpProps {
  values: TeamMemberSignUpSchemaType;
  trigger: UseFormTrigger<TeamMemberSignUpSchemaType>;
  inviteId?: string;
  token?: string;
}

export const useSubmitTeamMemberSignUp = ({
  trigger,
  values,
  inviteId,
  token,
}: useSubmitTeamMemberSignUpProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const newTeamMember = useOnSignUpTeamMemberStore(
    (state) => state.newTeamMember,
  );
  const { stripPhoneNumber } = useFormatHook();

  const onSignUpSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["getTeamMemberIsAuth"],
    });
    navigate(ROUTES.successAuth);
  };

  const {
    mutate: signUpTeamMemberMutation,
    isPending: isTeamMemberSignUpPending,
  } = useMutation({
    mutationKey: ["TeamMemberSignUpMutation"],
    mutationFn: signUpTeamMember,
    onSuccess: onSignUpSuccess,
  });

  const onSubmitSignUp = async () => {
    try {
      const isValidData = await trigger();

      if (!isValidData) return;

      if (!token) throw new Error("Invalid token");
      if (!inviteId) throw new Error("Invalid invite ID");
      if (!newTeamMember?.verificationToken || !newTeamMember?.phoneNumber)
        throw new Error("Invalid phone verification");

      const teamMember: TeamMemberSignUpData = {
        first_name: values.namesAndEmail.name,
        last_name: values.namesAndEmail.last_name,
        password: values.password.password,
        invite_id: inviteId,
        verification_token: newTeamMember.verificationToken,
        phone_number: {
          number: stripPhoneNumber(newTeamMember.phoneNumber),
          country_code: COUNTRY_CODES.US,
        },
      };

      signUpTeamMemberMutation({ teamMember, token });
    } catch (_error) {
      navigate(ROUTES.notFound);
    }
  };

  return {
    onSubmitSignUp,
    isTeamMemberSignUpPending,
  };
};
